import { chunk, orderBy, reject, slice, times } from 'lodash';
import { isAfter } from 'date-fns';
import React, {
  useCallback,
  useLayoutEffect,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';

import { useInterval } from '../hooks/use_interval';

// import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { Box, PageWrapper } from '../styles';

import Entries from '../pages/entries';
import Promo from '../pages/promo';
import NewEntries from '../pages/new_entries';
import TopEntries from '../pages/top_entries';

function SortedCarousel({
  animationEndedCallback,
  promoPages,
  questions,
  userEntries,
  promoPageTimer,
  entriesPageTimer,
  resultsPageTimer,
}) {
  const [pages, setPages] = useState([]);
  const [timer, setTimer] = useState(promoPageTimer);
  const [currentPage, setCurrentPage] = useState(1);

  // const shownEntries = useRef(new Set());
  const shownEntries = useRef(new Set(Object.keys(userEntries)));
  const [promoPageCount, setPromoPageCount] = useState();

  // Back and forward keyboard events
  useEffect(() => {
    const listener = event => {
      console.log('keyup', event);

      switch (event.key) {
        case 'ArrowLeft':
          if (currentPage > 1) {
            setCurrentPage(current => current - 1);
          } else {
            setCurrentPage(pages.length);
          }
          break;
        case 'ArrowRight':
          if (currentPage < pages.length) {
            setCurrentPage(current => current + 1);
          } else {
            setCurrentPage(pages.length);
          }
          break;
      }
    };

    document.addEventListener('keyup', listener);

    return () => {
      document.removeEventListener('keyup', listener);
    };
  });

  // Build promo pages
  const promoPagesPages = useMemo(() => {
    const now = Date.now();

    promoPages.sort((first, second) =>
      isAfter(first.attributes.created_at, second.attributes.created_at)
        ? 1
        : -1
    );

    return promoPages
      .map(({ id, attributes }) => ({
        key: id,
        type: 'promo',
        data: attributes,
      }))
      .filter(({ data: { expires_at = null } }) => {
        if (expires_at) {
          return isAfter(expires_at, now);
        } else {
          return true;
        }
      });
  }, [promoPages]);

  // Build entry pages
  const entryPages = useMemo(
    () =>
      times(
        Math.ceil(Object.keys(userEntries).length / 9),

        () => ({
          type: 'entries',
        })
      ),
    [userEntries]
  );

  // Build newEntries pages
  const newEntriesPages = useMemo(() => {
    const newEntries = reject(userEntries, (_, id) =>
      shownEntries.current.has(id)
    );

    newEntries.forEach(entry => {
      shownEntries.current.add(entry.user.id);
    });

    return chunk(newEntries, 3).map((group, index) => ({
      key: `results-${index}`,
      type: 'results',
      userEntries: group,
    }));
  }, [userEntries]);

  // Build topEntries page
  const topEntriesPage = useMemo(() => {
    const topEntries = slice(orderBy(userEntries, 'rank', 'asc'), 0, 3);

    if (false) {
      // topEntries) {
      return [
        {
          key: `topEntries`,
          type: 'topEntries',
          userEntries: topEntries,
        },
      ];
    } else {
      return [];
    }
  }, [userEntries]);

  // Concat all pages
  const buildPages = useCallback(() => {
    setPages([...promoPagesPages, ...entryPages, ...newEntriesPages]);
    // setPages([...promoPagesPages, ...topEntriesPage]);

    setPromoPageCount(promoPagesPages.length);
  }, [promoPagesPages, entryPages, newEntriesPages]);

  useLayoutEffect(buildPages, [buildPages]);

  const displayedPage = useMemo(
    () => pages[currentPage - 1],
    [pages, currentPage]
  );

  // Use specific timers for page types
  useEffect(() => {
    if (displayedPage && displayedPage.type) {
      switch (displayedPage.type) {
        case 'promo':
          setTimer(promoPageTimer);
          break;
        case 'results':
          setTimer(resultsPageTimer);
          break;
        case 'topEntries':
          setTimer(resultsPageTimer);
          break;
        default:
          setTimer(entriesPageTimer);
      }
    }
  }, [displayedPage, entriesPageTimer, promoPageTimer, resultsPageTimer]);

  // Page change timer
  useInterval(() => {
    if (currentPage < pages.length) {
      setCurrentPage(current => current + 1);
    } else {
      setCurrentPage(1);
      animationEndedCallback();
    }
  }, timer);

  return (
    <Box>
      {pages && pages.length > 0 && displayedPage ? (
        displayedPage.type === 'promo' ? (
          <PageWrapper key={displayedPage.key}>
            <Promo key={displayedPage.key} data={displayedPage.data} />
          </PageWrapper>
        ) : displayedPage.type === 'results' ? (
          <PageWrapper key={displayedPage.key}>
            <NewEntries userEntries={displayedPage.userEntries} />
          </PageWrapper>
        ) : displayedPage.type === 'topEntries' ? (
          <PageWrapper key={displayedPage.key}>
            <TopEntries topEntries={displayedPage.userEntries} />
          </PageWrapper>
        ) : displayedPage.type === 'entries' ? (
          <PageWrapper key={'entries-page'}>
            <Entries
              key="entry-page"
              userEntries={userEntries}
              questions={questions}
              offset={promoPageCount + 1}
              timer={currentPage}
            />
          </PageWrapper>
        ) : null
      ) : null}
    </Box>
  );
}

export default SortedCarousel;
